import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Copy, Users, X as XIcon } from "lucide-react";

const PLATFORMS = ["Discord", "WhatsApp", "Telegram", "Signal", "Slack", "Other"];

/**
 * Group Bazooka Panel — admin form + history list for shareable
 * chat-room invite links. Each claim grants the SAME benefits as the
 * 1:1 Bazooka (90 days Producer + 1600 cr), but a single URL can be
 * dropped into a private chat and burned through a configurable seat
 * cap (max_claims).
 *
 * Lives in its own component so ChairmansBazooka.jsx stays under the
 * 600-line cap; the parent renders us right under its primary form.
 */
const ChairmansBazookaGroupPanel = () => {
  const [title, setTitle] = useState("");
  const [platform, setPlatform] = useState("Discord");
  const [maxClaims, setMaxClaims] = useState(50);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [lastCreated, setLastCreated] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const r = await api.get("/admin/chairman/bazooka/group-links");
      setItems(r.data?.items || []);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { fetchList(); }, [fetchList]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!title.trim()) { toast.error("Chat-room title is required."); return; }
    if (!platform) { toast.error("Platform is required."); return; }
    const n = Number(maxClaims);
    if (!Number.isFinite(n) || n < 1 || n > 500) {
      toast.error("Max claims must be 1–500.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/chairman/bazooka/group-link", {
        chat_room_title: title.trim(),
        platform,
        max_claims: Math.floor(n),
      });
      setLastCreated(r.data);
      toast.success(`Group link created · ${n} seats`);
      setTitle("");
      setMaxClaims(50);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create group link");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it into the chat room");
    } catch (_) {
      toast.error("Couldn't copy — long-press the URL to select & copy");
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this group link? Existing claims keep their benefits; new clicks are blocked.")) return;
    try {
      await api.post(`/admin/chairman/bazooka/group-link/${token}/revoke`);
      toast.success("Group link revoked");
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke link");
    }
  };

  return (
    <div className="border border-amber-500/20 rounded-lg bg-amber-500/5 p-4 mt-6" data-testid="bazooka-group-panel">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-amber-400" />
        <h3 className="text-amber-200 font-semibold text-sm">Chat-room group link</h3>
        <span className="text-xs text-amber-300/70">single URL, N seats, full Founder bonus per claim</span>
      </div>

      <form onSubmit={handleCreate} className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-amber-200/80">Chat room title</Label>
          <Input
            value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Crypto Bros · Discord"
            maxLength={120}
            data-testid="group-bazooka-title"
            className="bg-gray-950/40 border-amber-500/30 text-white"
          />
        </div>
        <div>
          <Label className="text-xs text-amber-200/80">Platform</Label>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger
              className="bg-gray-950/40 border-amber-500/30 text-white"
              data-testid="group-bazooka-platform">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLATFORMS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-amber-200/80">Max claims</Label>
          <Input
            type="number" min={1} max={500}
            value={maxClaims} onChange={(e) => setMaxClaims(e.target.value)}
            data-testid="group-bazooka-max-claims"
            className="bg-gray-950/40 border-amber-500/30 text-white"
          />
        </div>
        <div className="sm:col-span-3">
          <Button
            type="submit" disabled={busy}
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            data-testid="group-bazooka-create-btn"
          >
            {busy ? "Creating…" : "Mint group link"}
          </Button>
        </div>
      </form>

      {lastCreated?.invite_url && (
        <div className="mt-4 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm" data-testid="group-bazooka-last-created">
          <div className="text-xs text-amber-200/70 mb-1">Latest link · paste into the chat room:</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs break-all bg-black/40 px-2 py-1 rounded">{lastCreated.invite_url}</code>
            <Button size="sm" variant="outline"
                    onClick={() => copyUrl(lastCreated.invite_url)}
                    className="border-amber-500/40 text-amber-200"
                    data-testid="group-bazooka-copy-latest">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-wider text-amber-300/70 mb-2">Recent group links</p>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {items.map((it) => {
              const seatsLeft = Math.max(0, (it.max_claims || 0) - (it.current_claims || 0));
              const revoked = !!it.revoked_at;
              return (
                <div key={it.token}
                     className="flex items-center justify-between gap-2 p-2 rounded bg-gray-900/60 border border-amber-500/15 text-xs"
                     data-testid={`group-bazooka-item-${it.token}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-amber-100 font-medium truncate">{it.chat_room_title}</div>
                    <div className="text-amber-300/60">
                      {it.platform} · {it.current_claims}/{it.max_claims} claimed · {seatsLeft} left
                      {revoked && <span className="text-red-300 ml-1">· revoked</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost"
                          onClick={() => copyUrl(it.invite_url)}
                          className="text-amber-200 hover:bg-amber-500/10"
                          data-testid={`group-bazooka-copy-${it.token}`}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  {!revoked && (
                    <Button size="sm" variant="ghost"
                            onClick={() => revoke(it.token)}
                            className="text-red-300 hover:bg-red-500/10"
                            data-testid={`group-bazooka-revoke-${it.token}`}>
                      <XIcon className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChairmansBazookaGroupPanel;
